import React, { useState, ChangeEvent, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { validatePassword } from '../../utils/passwordValidator';
import { supabase } from '../../lib/supabase';
import SignupWizardStepFields from './SignupWizardStepFields';

export type Step = 1 | 2 | 3 | 4;

export interface FormState {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  business_name: string;
  position_title: string;
  business_type: string;
  use_personal_for_business: boolean;
  business_address_line1: string;
  business_address_line2: string;
  business_city: string;
  business_state: string;
  business_postal_code: string;
  business_country: string;
  ein_number: string;
  consent_transactional: boolean;
  consent_marketing: boolean;
  accepted_terms: boolean;
}

const INITIAL_FORM: FormState = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  password: '',
  confirmPassword: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  postal_code: '',
  country: '',
  business_name: '',
  position_title: '',
  business_type: '',
  use_personal_for_business: true,
  business_address_line1: '',
  business_address_line2: '',
  business_city: '',
  business_state: '',
  business_postal_code: '',
  business_country: '',
  ein_number: '',
  consent_transactional: false,
  consent_marketing: false,
  accepted_terms: false,
};

const SignupWizard: React.FC = () => {
  const { register, error } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>(1);
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [einFile, setEinFile] = useState<File | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, type, checked, value } = e.target;
    const val = type === 'checkbox' ? checked : value;
    setForm(prev => ({ ...prev, [name]: val }));
  };

  const handleEinChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setEinFile(file);
  };

  const validateStep = (current: Step): boolean => {
    setLocalError(null);

    if (current === 1) {
      if (!form.first_name || !form.last_name || !form.email || !form.phone || !form.password || !form.confirmPassword) {
        setLocalError('Please complete all fields on this step.');
        return false;
      }
      if (form.password !== form.confirmPassword) {
        setLocalError('Passwords do not match.');
        return false;
      }
      const result = validatePassword(form.password);
      if (!result.isValid) {
        setLocalError('Password does not meet the requirements.');
        return false;
      }
    }

    if (current === 2) {
      if (!form.address_line1 || !form.city || !form.state || !form.postal_code || !form.country) {
        setLocalError('Please provide your full address.');
        return false;
      }
    }

    if (current === 3) {
      if (!form.business_name || !form.position_title || !form.business_type) {
        setLocalError('Please provide your business information.');
        return false;
      }
      if (!form.use_personal_for_business) {
        if (!form.business_address_line1 || !form.business_city || !form.business_state || !form.business_postal_code || !form.business_country) {
          setLocalError('Please provide a full business address or select to use your personal address.');
          return false;
        }
      }
      if (!einFile) {
        setLocalError('Please upload your IRS EIN letter.');
        return false;
      }
    }

    if (current === 4) {
      if (!form.consent_transactional || !form.consent_marketing || !form.accepted_terms) {
        setLocalError('Please review and accept all terms and consent checkboxes.');
        return false;
      }
    }

    return true;
  };

  const goNext = () => {
    if (validateStep(step) && step < 4) {
      setStep((step + 1) as Step);
    }
  };

  const goBack = () => {
    if (step > 1) {
      setStep((step - 1) as Step);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateStep(4)) return;

    setSubmitting(true);
    try {
      await register(form.email, form.password, form.first_name, form.last_name);

      // Wait a moment for session to be established after registration
      await new Promise(resolve => setTimeout(resolve, 500));

      // Get user ID and update profile with consent timestamps
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const consentTimestamp = new Date().toISOString();
        const updates: any = {};

        if (form.accepted_terms) {
          updates.terms_accepted_at = consentTimestamp;
        }
        if (form.consent_transactional) {
          updates.consent_transactional_accepted_at = consentTimestamp;
        }
        if (form.consent_marketing) {
          updates.consent_marketing_accepted_at = consentTimestamp;
        }

        // Update profile with consent timestamps
        if (Object.keys(updates).length > 0) {
          const { error: updateError } = await supabase
            .from('profiles')
            .update(updates)
            .eq('id', user.id);

          if (updateError) {
            console.error('Error updating consent timestamps:', updateError);
          }
        }
      }

      // Upload EIN file to Supabase Storage if provided
      let fileUrl = null;
      let fileName = null;
      let fileType = null;

      if (einFile) {
        try {
          // Wait a moment for session to be established after registration
          await new Promise(resolve => setTimeout(resolve, 500));

          // Get user ID after registration
          const { data: { user }, error: userError } = await supabase.auth.getUser();

          if (userError) {
            console.error('Error getting user after registration:', userError);
          }

          if (user) {
            // Create a unique file path
            const fileExt = einFile.name.split('.').pop();
            const fileNameWithExt = `ein-${user.id}-${Date.now()}.${fileExt}`;
            const filePath = `signup-documents/${user.id}/${fileNameWithExt}`;

            // Upload file to Supabase Storage
            const { error: uploadError } = await supabase.storage
              .from('client-submissions')
              .upload(filePath, einFile, {
                cacheControl: '3600',
                upsert: false
              });

            if (uploadError) {
              console.error('Error uploading EIN file:', uploadError);
              // Still continue with signup even if upload fails
            } else {
              // Get public URL
              const { data: urlData } = supabase.storage
                .from('client-submissions')
                .getPublicUrl(filePath);

              fileUrl = urlData.publicUrl;
              fileName = einFile.name;
              fileType = einFile.type;
            }
          }
        } catch (fileErr) {
          console.error('Exception during EIN file upload:', fileErr);
          // Don't block signup if file upload fails
        }
      }

      // Fire-and-forget signup notification to backend with ALL business data
      try {
        await fetch('/api/signup/notify', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            first_name: form.first_name,
            last_name: form.last_name,
            email: form.email,
            phone: form.phone,
            business_name: form.business_name,
            position_title: form.position_title,
            business_type: form.business_type,
            ein_number: form.ein_number,
            file_url: fileUrl,
            file_name: fileName,
            file_type: fileType,
            address: {
              line1: form.address_line1,
              line2: form.address_line2,
              city: form.city,
              state: form.state,
              postal_code: form.postal_code,
              country: form.country,
            },
            business_address: form.use_personal_for_business ? null : {
              line1: form.business_address_line1,
              line2: form.business_address_line2,
              city: form.business_city,
              state: form.business_state,
              postal_code: form.business_postal_code,
              country: form.business_country,
            },
            consent_data: {
              terms_accepted: form.accepted_terms,
              consent_transactional: form.consent_transactional,
              consent_marketing: form.consent_marketing,
              consent_timestamp: new Date().toISOString(),
            },
          }),
        });
      } catch {
        // Do not block signup on notification failure
      }

      // After signup, send the user to billing so they must choose a plan or promo
      navigate('/billing');
    } catch (err) {
      console.error('Registration failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-2xl bg-white dark:bg-slate-900 rounded-2xl shadow-2xl shadow-black/40 border border-white/10 px-6 py-6 space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Create your Nexus account</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">Step {step} of 4</p>
        </div>
        <div className="text-sm text-slate-600 dark:text-slate-300">
          Already have an account?{' '}
          <Link to="/login" className="text-primary-500 hover:text-primary-400 font-semibold">
            Log in
          </Link>
        </div>
      </div>

      {localError && <div className="text-sm text-red-600 dark:text-red-400">{localError}</div>}
      {error && <div className="text-sm text-red-600 dark:text-red-400">{error}</div>}

      <form onSubmit={handleSubmit} className="space-y-6" autoComplete="off">
        {/* Step content */}
        <div className="space-y-4">
          <SignupWizardStepFields
            step={step}
            form={form}
            onChange={handleChange}
            onEinChange={handleEinChange}
            showPassword={showPassword}
            showConfirmPassword={showConfirmPassword}
            onTogglePassword={() => setShowPassword(prev => !prev)}
            onToggleConfirmPassword={() => setShowConfirmPassword(prev => !prev)}
          />
        </div>

        {/* Navigation buttons */}
        <div className="flex justify-between pt-4">
          <button
            type="button"
            onClick={goBack}
            disabled={step === 1}
            className="inline-flex items-center px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-sm text-slate-700 dark:text-slate-200 disabled:opacity-50"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Back
          </button>
          {step < 4 ? (
            <button
              type="button"
              onClick={goNext}
              className="inline-flex items-center px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-500 text-sm font-semibold text-white"
            >
              Next
              <ArrowRightIcon className="h-4 w-4 ml-1" />
            </button>
          ) : (
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-500 text-sm font-semibold text-white disabled:opacity-60"
            >
              {submitting ? 'Creating account...' : 'Create account'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default SignupWizard;
